import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { saveAs } from 'file-saver';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { RoleContext } from 'src/context/roleContext';
import { latinToCyrillic } from 'src/helper/helperFunction';


const PageWrapper = styled(Box)({ padding: '24px' });

const HeaderPaper = styled(Paper)({
  padding: '24px 28px',
  borderRadius: '16px',
  marginBottom: '20px',
  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 4px 20px rgba(21,101,192,0.3)',
  flexWrap: 'wrap',
  gap: '12px',
});

const ContentPaper = styled(Paper)({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  padding: '16px',
});

const FilterBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  flexWrap: 'wrap',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8faff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
  },
});

const awardColors = {
  'I': { bg: '#fff8e1', color: '#f57f17', label: 'schoolDiplomasPage.awardLabels.awardI' },
  'II': { bg: '#e8f5e9', color: '#2e7d32', label: 'schoolDiplomasPage.awardLabels.awardII' },
  'III': { bg: '#fce4ec', color: '#c62828', label: 'schoolDiplomasPage.awardLabels.awardIII' },
  'Похвала': { bg: '#e3f2fd', color: '#1565c0', label: 'schoolDiplomasPage.awardLabels.awardPohvala' },
  'Zlatna': { bg: '#fff8e1', color: '#f57f17', label: 'schoolDiplomasPage.awardLabels.awardZlatna' },
  'Zahvalnica': { bg: '#e8f5e9', color: '#2e7d32', label: 'schoolDiplomasPage.awardLabels.awardZahvalnica' },
};

const TYPE_CONFIG = {
  '1': { label: 'ZZ', fullKey: 'schoolDiplomasPage.typeLabels.zlatna', bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
  '2': { label: 'PR', fullKey: 'schoolDiplomasPage.typeLabels.prviRang', bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  '3': { label: 'F', fullKey: 'schoolDiplomasPage.typeLabels.finalniRang', bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8' },
  '4': { label: 'Z', fullKey: 'schoolDiplomasPage.typeLabels.zahvalnica', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

const classLabels = {
  1: 'I OS', 2: 'II OS', 3: 'III OS', 4: 'IV OS',
  5: 'V OS', 6: 'VI OS', 7: 'VII OS', 8: 'VIII OS',
  9: 'I SS', 10: 'II SS', 11: 'III SS', 12: 'IV SS',
};

const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => String(2026 + i));

const safeFilename = (s) => (s || '').replace(/[^\p{L}\p{N} \-]/gu, '_').trim();


const SchoolDiplomasPage = () => {
  const { t } = useTranslation();

  const [schoolId, setSchoolId] = useState(null);
  const [diplomas, setDiplomas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [zlatnaDownloading, setZlatnaDownloading] = useState(false);
  const [zahvalnicaDownloading, setZahvalnicaDownloading] = useState(false);
  const [singleLoading, setSingleLoading] = useState(null);

  const currentYear = String(new Date().getFullYear());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [searchText, setSearchText] = useState('');
  const [filterClass, setFilterClass] = useState(null);
  const [filterAward, setFilterAward] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);

  const { schoolID } = useContext(RoleContext);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const userId = decoded.user_id || '';
        const idMatch = String(userId).match(/\d+/);
        if (idMatch) setSchoolId(idMatch[0]);
      } catch { }
    }
  }, []);

  const fetchDiplomas = useCallback((year) => {
    if (!schoolId) return;
    setLoading(true);
    const yearParam = year && year !== 'All' ? `&year=${encodeURIComponent(year)}` : '';
    fetch(`${API_BASE_URL}/api/schoolDiplomas.php?action=list&school_id=${schoolId}${yearParam}`)
      .then(r => r.json())
      .then(data => setDiplomas(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [schoolId]);

  const fetchClasses = useCallback((year) => {
    if (!schoolId) return;
    const yearParam = year && year !== 'All' ? `&year=${encodeURIComponent(year)}` : '';
    fetch(`${API_BASE_URL}/api/schoolDiplomas.php?action=classes&school_id=${schoolId}${yearParam}`)
      .then(r => r.json())
      .then(data => setAvailableClasses(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [schoolId]);

  useEffect(() => {
    if (schoolId) {
      fetchDiplomas(selectedYear);
      fetchClasses(selectedYear);
      setFilterClass(null);
      setFilterAward(null);
    }
  }, [schoolId, selectedYear, fetchDiplomas, fetchClasses]);

  const hasZlatna = diplomas.some(d => String(d.template_type) === '1');
  const hasZahvalnica = diplomas.some(d => String(d.template_type) === '4');

  const availableAwards = [...new Set(
    diplomas
      .filter(d => String(d.template_type) !== '1' && String(d.template_type) !== '4' && d.award)
      .map(d => d.award)
  )];

  const filteredDiplomas = diplomas.filter(d => {
    if (String(d.template_type) === '1' || String(d.template_type) === '4') return false;
    if (filterClass !== null && d.class !== filterClass) return false;
    if (filterAward !== null && d.award !== filterAward) return false;
    const q = searchText.toLowerCase().trim();
    if (!q) return true;
    const qCyr = latinToCyrillic(q);
    const name = (d.student_name || '').toLowerCase();
    return name.includes(q) || name.includes(qCyr);
  });


  const handleDownloadSingle = async (studentId, studentName, templateType, diplomaYear) => {
    setSingleLoading(studentId);
    try {
      const url = `${API_BASE_URL}/api/schoolDiplomas.php?action=downloadSingle&school_id=${schoolId}&student_id=${studentId}&template_type=${encodeURIComponent(templateType)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();

      const base = safeFilename(studentName) || 'diploma';
      const year = diplomaYear || (selectedYear !== 'All' ? selectedYear : '');
      const yearSuffix = year ? `_${year}` : '';
      let filename;
      if (String(templateType) === '2') filename = `${base}_prvi_rang${yearSuffix}.pdf`;
      else if (String(templateType) === '3') filename = `${base}_finale${yearSuffix}.pdf`;
      else filename = `${base}${yearSuffix}.pdf`;

      saveAs(blob, filename);
    } catch (e) {
      console.error('Single diploma download error:', e);
    } finally {
      setSingleLoading(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const yearParam = selectedYear && selectedYear !== 'All' ? `&year=${encodeURIComponent(selectedYear)}` : '';
      const url = `${API_BASE_URL}/api/schoolDiplomas.php?action=downloadAll&school_id=${schoolId}${yearParam}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const yearSuffix = selectedYear && selectedYear !== 'All' ? `_${selectedYear}` : '';
      const anyRow = diplomas.find(d => d.school_name);
      const schoolName = safeFilename(anyRow?.school_name || 'skola');
      saveAs(blob, `${schoolName}_diplomas${yearSuffix}.zip`);
    } catch (e) {
      console.error('Download all error:', e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadZlatna = async () => {
    setZlatnaDownloading(true);
    try {
      const yearParam = selectedYear && selectedYear !== 'All' ? `&year=${encodeURIComponent(selectedYear)}` : '';
      const url = `${API_BASE_URL}/api/schoolDiplomas.php?action=downloadZlatna&school_id=${schoolId}${yearParam}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();

      const zzRow = diplomas.find(d => String(d.template_type) === '1');
      const schoolName = safeFilename(zzRow?.school_name || 'skola');
      const year = selectedYear && selectedYear !== 'All'
        ? selectedYear
        : (zzRow?.diploma_year || '');
      const yearSuffix = year ? `_${year}` : '';
      saveAs(blob, `${schoolName}_zlatna${yearSuffix}.pdf`);
    } catch (e) {
      console.error('Download zlatna error:', e);
    } finally {
      setZlatnaDownloading(false);
    }
  };

  const handleDownloadZahvalnica = async () => {
    setZahvalnicaDownloading(true);
    try {
      const yearParam = selectedYear && selectedYear !== 'All' ? `&year=${encodeURIComponent(selectedYear)}` : '';
      const url = `${API_BASE_URL}/api/schoolDiplomas.php?action=downloadZahvalnica&school_id=${schoolId}${yearParam}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();

      const zRow = diplomas.find(d => String(d.template_type) === '4');
      const schoolName = safeFilename(zRow?.school_name || 'skola');
      const year = selectedYear && selectedYear !== 'All'
        ? selectedYear
        : (zRow?.diploma_year || '');
      const yearSuffix = year ? `_${year}` : '';
      saveAs(blob, `${schoolName}_zahvalnica${yearSuffix}.pdf`);
    } catch (e) {
      console.error('Download zahvalnica error:', e);
    } finally {
      setZahvalnicaDownloading(false);
    }
  };


  const columns = [
    {
      field: 'rowIndex',
      headerName: '#',
      width: 60,
      sortable: false,
      renderCell: (params) => {
        const idx = filteredDiplomas.findIndex(d => d.id === params.row.id);
        return (
          <Typography sx={{ color: '#999', fontWeight: 600, fontSize: '14px' }}>
            {idx + 1}
          </Typography>
        );
      },
    },
    {
      field: 'student_name',
      headerName: t('columns.studentName'),
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1a237e' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'class',
      headerName: t('columns.class'),
      width: 110,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '14px', color: '#555' }}>
          {classLabels[params.value] || (params.value ? `Разред ${params.value}` : '—')}
        </Typography>
      ),
    },
    {
      field: 'template_type',
      headerName: t('columns.diplomaType'),
      width: 140,
      renderCell: (params) => {
        const typeKey = String(params.value || '');
        const typeCfg = TYPE_CONFIG[typeKey] || {
          label: typeKey || '—', full: typeKey || '—',
          bg: '#f5f5f5', color: '#555', border: '#ddd',
        };
        const typeLabel = typeCfg.fullKey ? t(typeCfg.fullKey) : typeCfg.full;
        return typeKey ? (
          <Tooltip title={typeLabel}>
            <Box sx={{
              display: 'inline-flex', px: 1, py: 0.25, borderRadius: '6px',
              fontSize: '12px', fontWeight: 700,
              backgroundColor: typeCfg.bg, color: typeCfg.color,
              border: `1px solid ${typeCfg.border}`,
              cursor: 'default', userSelect: 'none', whiteSpace: 'nowrap',
            }}>
              {typeCfg.label}
            </Box>
          </Tooltip>
        ) : (
          <Typography sx={{ color: '#bbb', fontSize: '14px' }}>—</Typography>
        );
      },
    },
    {
      field: 'point_sum',
      headerName: t('columns.point_sum'),
      width: 100,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: '#1976d2', fontSize: '14px' }}>
          {params.value != null ? String(params.value).replace('.', ',') : '—'}
        </Typography>
      ),
    },
    {
      field: 'award',
      headerName: t('columns.award'),
      width: 130,
      renderCell: (params) => {
        const awardStyle = awardColors[params.value] || { bg: '#f5f5f5', color: '#555', label: params.value };
        return (
          <Chip
            label={awardStyle.label ? t(awardStyle.label) : params.value}
            size="small"
            sx={{
              backgroundColor: awardStyle.bg,
              color: awardStyle.color,
              fontWeight: 700,
              fontSize: '12px',
              border: `1px solid ${awardStyle.color}33`,
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: t('columns.diploma'),
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={t('schoolDiplomasPage.downloadTooltip')}>
          <span>
            <IconButton
              size="small"
              onClick={() => handleDownloadSingle(
                params.row.student_id,
                params.row.student_name,
                params.row.template_type,
                params.row.diploma_year,
              )}
              disabled={singleLoading === params.row.student_id}
              sx={{ color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              {singleLoading === params.row.student_id
                ? <CircularProgress size={18} />
                : <PictureAsPdfIcon />}
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];


  if (!schoolId) {
    return (
      <PageWrapper>
        <Typography color="error">{t('schoolDiplomasPage.noSchoolFromAccount')}</Typography>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderPaper elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmojiEventsIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>{t('schoolDiplomasPage.title')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t('schoolDiplomasPage.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            options={YEAR_OPTIONS}
            value={selectedYear}
            onChange={(_, v) => {
              if (v) {
                setSelectedYear(v);
                setFilterClass(null);
                setFilterAward(null);
              }
            }}
            disableClearable
            size="small"
            sx={{
              minWidth: 120,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiAutocomplete-popupIndicator': { color: '#fff' },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.85)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#fff' },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('schoolDiplomasPage.yearLabel')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <CalendarMonthIcon sx={{ fontSize: 18, color: '#fff', mr: 0.5 }} />,
                }}
                sx={{ '& .MuiInputBase-input': { color: '#fff' } }}
              />
            )}
          />

          <Tooltip title={!hasZlatna ? t('schoolDiplomasPage.zlatnaTooltipUnavailable') : t('schoolDiplomasPage.zlatnaTooltipAvailable')}>
            <span>
              <Button
                variant="contained"
                onClick={handleDownloadZlatna}
                disabled={zlatnaDownloading || !hasZlatna}
                startIcon={zlatnaDownloading ? <CircularProgress size={18} color="inherit" /> : <StarIcon />}
                sx={{
                  backgroundColor: hasZlatna ? 'rgba(255,215,0,0.25)' : 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(8px)',
                  color: hasZlatna ? '#fff' : 'rgba(255,255,255,0.35)',
                  fontWeight: 700,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  px: 2,
                  border: hasZlatna ? '1px solid rgba(255,215,0,0.6)' : '1px solid rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: hasZlatna ? 'rgba(255,215,0,0.38)' : 'rgba(255,255,255,0.08)' },
                  '&:disabled': {
                    color: 'rgba(255,255,255,0.35)',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  },
                }}
              >
                {zlatnaDownloading ? t('loading.loading') : t('schoolDiplomasPage.downloadZlatnaButton')}
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={!hasZahvalnica ? t('schoolDiplomasPage.zahvalnicaTooltipUnavailable') : t('schoolDiplomasPage.zahvalnicaTooltipAvailable')}>
            <span>
              <Button
                variant="contained"
                onClick={handleDownloadZahvalnica}
                disabled={zahvalnicaDownloading || !hasZahvalnica}
                startIcon={zahvalnicaDownloading ? <CircularProgress size={18} color="inherit" /> : <SchoolIcon />}
                sx={{
                  backgroundColor: hasZahvalnica ? 'rgba(102,187,106,0.25)' : 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(8px)',
                  color: hasZahvalnica ? '#fff' : 'rgba(255,255,255,0.35)',
                  fontWeight: 700,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  px: 2,
                  border: hasZahvalnica ? '1px solid rgba(102,187,106,0.6)' : '1px solid rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: hasZahvalnica ? 'rgba(102,187,106,0.38)' : 'rgba(255,255,255,0.08)' },
                  '&:disabled': {
                    color: 'rgba(255,255,255,0.35)',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  },
                }}
              >
                {zahvalnicaDownloading ? t('loading.loading') : t('schoolDiplomasPage.downloadZahvalnicaButton')}
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            onClick={handleDownloadAll}
            disabled={downloading || filteredDiplomas.length === 0}
            startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)',
              color: '#fff',
              fontWeight: 700,
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: '15px',
              px: 3,
              border: '1px solid rgba(255,255,255,0.35)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
              '&:disabled': { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' },
            }}
          >
            {downloading ? t('loading.loading') : t('schoolDiplomasPage.downloadAllDiplomas')}
          </Button>
        </Box>
      </HeaderPaper>

      <ContentPaper>
        {diplomas.length === 0 && !loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <EmojiEventsIcon sx={{ fontSize: 56, color: '#ccc', mb: 2 }} />
            <Typography color="text.secondary" variant="h6">
              {t('schoolDiplomasPage.emptyState')}
            </Typography>
          </Box>
        ) : (
          <>
            <FilterBar>
              <StyledTextField
                label={t('schoolDiplomasPage.searchPlaceholder')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="small"
                sx={{ minWidth: '240px' }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#1976d2', mr: 0.5, fontSize: 20 }} />,
                }}
              />

              <Autocomplete
                size="small"
                sx={{ minWidth: 150 }}
                options={['All', ...availableClasses]}
                value={filterClass ?? 'All'}
                onChange={(_, v) => {
                  if (v === 'All' || !v) {
                    setFilterClass(null);
                  } else {
                    setFilterClass(v);
                  }
                }}
                getOptionLabel={(o) => o === 'All' ? t('general.all') : o.toString()}
                renderInput={(params) => (
                  <StyledTextField {...params} label={t('columns.class')} />
                )}
              />

              <Autocomplete
                size="small"
                sx={{ minWidth: 160 }}
                options={['All', ...availableAwards]}
                value={filterAward ?? 'All'}
                onChange={(_, v) => {
                  if (v === 'All' || !v) {
                    setFilterAward(null);
                  } else {
                    setFilterAward(v);
                  }
                }}
                getOptionLabel={(o) => {
                  if (o === 'All') return t('general.all');
                  const cfg = awardColors[o];
                  return cfg ? t(cfg.label) : o;
                }}
                renderInput={(params) => (
                  <StyledTextField {...params} label={t('columns.award')} />
                )}
                renderOption={(props, opt) => {
                  if (opt === 'All') return <li {...props}>{t('general.all')}</li>;

                  const cfg = awardColors[opt] || { bg: '#f5f5f5', color: '#555', label: opt };
                  return (
                    <li {...props}>
                      <Chip
                        label={cfg.label ? t(cfg.label) : opt}
                        size="small"
                        sx={{
                          backgroundColor: cfg.bg,
                          color: cfg.color,
                          fontWeight: 700,
                          fontSize: '12px',
                          border: `1px solid ${cfg.color}33`,
                        }}
                      />
                    </li>
                  );
                }}
              />
            </FilterBar>

            <DataGrid
              rows={filteredDiplomas}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
              pageSizeOptions={[5, 10, 20]}
              loading={loading}
              autoHeight
              disableColumnMenu
              disableRowSelectionOnClick
              componentsProps={{
                pagination: {
                  labelRowsPerPage: t('pagination.rowsPerPage'),
                  labelDisplayedRows: ({ from, to, count }) =>
                    `${from}-${to} ${t('pagination.of')} ${count !== -1 ? count : `${t('pagination.moreThan')} ${to}`}`,
                },
              }}
              sx={{
                border: 'none',
                borderRadius: '12px',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  borderRadius: '10px 10px 0 0',
                  borderBottom: 'none',
                },
                '& .MuiDataGrid-columnHeader': { outline: 'none !important' },
                '& .MuiDataGrid-cell': {
                  color: '#1a237e',
                  fontSize: '14px',
                  borderBottom: '1px solid #e8edf5',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#e3f2fd' },
                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#f8faff' },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: '#e3f2fd',
                  borderTop: '1px solid #ddd',
                  borderRadius: '0 0 12px 12px',
                },
                '& .MuiDataGrid-sortIcon': { color: '#fff' },
                '& .MuiDataGrid-menuIconButton': { color: '#fff' },
              }}
            />
          </>
        )}
      </ContentPaper>
    </PageWrapper>
  );
};

export default SchoolDiplomasPage;